import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Button,
  Input,
  Flex,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Text,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  Badge,
  Switch,
  InputLeftElement
 }from "@chakra-ui/react";
import { MdEdit, MdDone, MdAdd, MdVisibility, MdVisibilityOff, MdOutlineFilterAltOff, MdSearch } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import CreateUser from "views/admin/Users/components/CreateUser";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { toLocalTime } from "utils/time";
import { visibleRolesFor, getCurrentUserId, canEditUserRole } from "utils/roleVisibility";

const USERS_PAGE_ID = PAGE_IDS.USERS;
const ROLES_PAGE_ID = PAGE_IDS.ROLES;

const UsersList = () => {
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editableUser, setEditableUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const toast = useToast();
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [search, setSearch] = useState("");
  const [isSavingUser, setIsSavingUser] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10); // Now controlled by select
  const [totalPages, setTotalPages] = useState(1);

  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });
  const toggleCreateUserModal = () => {
    setShowCreateUserModal(!showCreateUserModal);
  };
  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === USERS_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast]);


// Edit button handler
const handleEditUser = (user) => {
  setEditableUser({
    ...user,
    roleId: user.role?.id ?? user.roleId,
  });
  setShowEditUserModal(true);
};

// Save handler
const handleSaveUser = async () => {
  setIsSavingUser(true);
  try {
    // Mirrors the field-level isDisabled rules below: a user can't change
    // their own email/role, and only Admins/Super Admin can reassign anyone's
    // role — so only include these when the field was actually editable,
    // rather than trusting whatever's sitting in local state.
    const data = {
      page_id: USERS_PAGE_ID,
      userId: editableUser.id,
      firstName: editableUser.firstName,
      lastName: editableUser.lastName,
      ...(editableUser.password && { password: editableUser.password }),
      isActive: editableUser.isActive,
      ...(!isEditingSelf && { email: editableUser.email }),
      ...(canEditRoleField && { roleId: Number(editableUser.roleId) }),
    };
    const response = await fetchWithToken(
      `${process.env.REACT_APP_NEWS_API_URL}/editUser`,
      "PUT",
      data
    );
    // fetchWithToken already throws on a non-2xx response, so getting here
    // means the request succeeded — don't also gate on a `status: "success"`
    // field, since /editUser's actual response body doesn't reliably have one.
    if (!response) {
      throw new Error("Failed to update user");
    }
    setUsers((prev) => prev.map((u) => (u.id === editableUser.id ? { ...u, ...data } : u)));
    setEditableUser(null);
    setShowEditUserModal(false);
    toast({
      title: "Success",
      description: "User updated successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  } catch (error) {
    toast({
      title: "Error updating user.",
      description: error.message || "Failed to update user",
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  } finally {
    setIsSavingUser(false);
  }
};

// Close modal handler
const handleEditFormClose = () => {
  setEditableUser(null);
  setShowEditUserModal(false);
};

  const fetchUsers = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_NEWS_API_URL}/users?page_id=${USERS_PAGE_ID}&page=${currentPage}&limit=${ordersPerPage}&search=${search}`;
      const responseUsers = await fetchWithToken(url);
      const responseRoles = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/role?page_id=${ROLES_PAGE_ID}`
      );

      if (responseUsers && responseRoles) {
        setUsers(responseUsers.data);
        setRoles(visibleRolesFor(responseRoles.data || []));
        if (responseUsers.meta) {
          setTotalPages(responseUsers.meta.last_page || 1);
        }
      } else {
        console.error("Error fetching users or roles");
      }
    } catch (error) {
      console.error("Error fetching users or roles:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, ordersPerPage, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Called by CreateUser after a successful create — refreshes the list in
  // place (React re-render) instead of a full page reload.
  const handleUserCreated = () => {
    setShowCreateUserModal(false);
    fetchUsers();
  };

  // Editing your own account: role and email are locked (no self-promotion,
  // no accidental self-lockout). Editing someone else's role additionally
  // requires being an Admin or the Super Admin — Editors etc. can't
  // reassign anyone's role.
  const isEditingSelf = !!editableUser && Number(editableUser.id) === getCurrentUserId();
  const canEditRoleField = !isEditingSelf && canEditUserRole();

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Box
        overflowY="auto"
        maxHeight="400px"
        px="25px"
        mb="20px"
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "flex-start", md: "center" }}
      >
        <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Users List")}
          </Text>
          <Button
            onClick={toggleCreateUserModal}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
            transition="all 0.2s"
          >
            {t("Add User")}
          </Button>
        </Flex>
       <Flex alignItems="center" gap={4} mb={{ base: 4, md: 0 }} ml="auto" mr={"4px"}>
  <FormControl maxW={{ base: "100%", md: "120px" }}>
    <Select
      value={ordersPerPage}
      onChange={(e) => {
        setOrdersPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
      }}
      size="sm"
      borderColor={GOLD_BORDER}
      focusBorderColor={GOLD_BORDER}
      borderRadius={"md"}
      _hover={{ borderColor: GOLD_BORDER }}
    >
      <option value="10">10 {t("per page")}</option>
      <option value="20">20 {t("per page")}</option>
      <option value="50">50 {t("per page")}</option>
    </Select>
  </FormControl>
  <FormControl maxW="230px">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <MdSearch color="gray.400" />
            </InputLeftElement>
            <Input
               size="sm"
              w={{ base: "100%", md: "230px" }}
              mr={4}
              borderColor={GOLD_BORDER}
              focusBorderColor={GOLD_BORDER}
              placeholder={t("Search by name or email")}
              value={search}
              _hover={{ borderColor: GOLD_BORDER }}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </FormControl>
              <Button
                  size="sm"
                  leftIcon={<MdOutlineFilterAltOff />}
                  variant="outline"
                  colorScheme="red"
                  onClick={() => {
                            setSearch("");
                            setCurrentPage(1);
                            setOrdersPerPage(10);

                  }}
                  _hover={{
                    borderColor: "red.500",
                    color: "red.500",
                    backgroundColor: "rgba(245, 101, 101, 0.1)",
                    boxShadow: "0 0 0 2px #F56565",
                  }}
                >

                  {t("Reset")}
                </Button>
</Flex>


      </Box>
      <Box px="20px">
 <Box  overflowY="auto" maxHeight="400px" px="20px">
            {loading ? (
            <Spinner size="xl" />
          ) : (

               <Table variant="simple">
                 <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                      <Tr bg={tableHeaderBg} style={{ position: 'sticky', top: 0, zIndex: 2 }}>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Index")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("First Name")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Last Name")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Email")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Role")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Last Login")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
      <Th textColor={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
    </Tr>
  </Thead>
  <Tbody>
    {users.map((user, index) => (
      <Tr key={user.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
        <Td>{(currentPage - 1) * ordersPerPage + index + 1}</Td>
        <Td>{user.firstName}</Td>
        <Td>{user.lastName}</Td>
        <Td>{user.email}</Td>
        <Td>{user.role?.roleName}</Td>
        <Td>
          {user.lastLoginAt ? toLocalTime(user.lastLoginAt) : t("Never")}
        </Td>
        <Td>
          <Badge
            colorScheme={user && user.isActive === true ? "green" : "gray"}
            variant="solid"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="0.85em"
          >
            {user && user.isActive === true ? t("Active") : t("Inactive")}
          </Badge>
        </Td>
        <Td>
          <Flex>
          <Button
            variant="outline"
            borderColor={GOLD_BORDER}
            color={GOLD_TEXT}
            size="sm"
            isDisabled={!permissions.canEdit}
            onClick={() => handleEditUser(user)}
            leftIcon={<MdEdit />}
            _hover={{ bg: GOLD_LIGHT }}
          >
            {t("Edit")}
          </Button>
          </Flex>
        </Td>
      </Tr>
    ))}
  </Tbody>
</Table>
        )}
      </Box>
       {/* Pagination Controls */}
        {totalPages > 0 && (
          <Box mt={4} mb={4}>
            {/* Pagination Info */}
            <Text textAlign="center" fontSize="sm" color="gray.600" mb={3}>
              {t("Page")} {currentPage} {t("of")} {totalPages}
              {users.length > 0 && (
                <span> - {t("Showing")} {((currentPage - 1) * ordersPerPage) + 1} {t("to")} {Math.min(currentPage * ordersPerPage, ((currentPage - 1) * ordersPerPage) + users.length)} {t("results")}</span>
              )}
            </Text>

            {/* Pagination Buttons */}
            <Flex justifyContent="center" wrap="wrap" gap={2}>
              {/* First Page */}
              <Button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                size="sm"
                variant="outline"
                borderColor="gray.200"
                color="gray.700"
                borderRadius="full"
                _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
                display={{ base: currentPage > 3 ? "block" : "none", md: currentPage > 2 ? "block" : "none" }}
              >
                {t("First")}
              </Button>

              {/* Previous Page */}
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                size="sm"
                variant="outline"
                borderColor="gray.200"
                color="gray.700"
                borderRadius="full"
                _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
              >
                {t("Previous")}
              </Button>

              {/* Page Numbers with smart ellipsis */}
              {(() => {
                const delta = 2; // Number of pages to show around current page
                const range = [];
                const rangeWithDots = [];

                for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                  range.push(i);
                }

                if (currentPage - delta > 2) {
                  rangeWithDots.push(1, '...');
                } else {
                  rangeWithDots.push(1);
                }

                rangeWithDots.push(...range);

                if (currentPage + delta < totalPages - 1) {
                  rangeWithDots.push('...', totalPages);
                } else if (totalPages > 1) {
                  rangeWithDots.push(totalPages);
                }

                return rangeWithDots.map((page, index) => {
                  if (page === '...') {
                    return (
                      <Text key={`ellipsis-${index}`} mx={2} alignSelf="center" fontSize="sm" color="gray.500">
                        ...
                      </Text>
                    );
                  }

                  const isActivePage = currentPage === page;
                  return (
                    <Button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      variant={isActivePage ? undefined : "outline"}
                      bg={isActivePage ? GOLD_BORDER : undefined}
                      color={isActivePage ? "#1A1A1A" : "gray.700"}
                      borderColor={isActivePage ? undefined : "gray.200"}
                      _hover={isActivePage ? { bg: GOLD_TEXT, color: "white" } : { borderColor: GOLD_TEXT, color: GOLD_TEXT }}
                      borderRadius="full"
                      size="sm"
                      minW="40px"
                    >
                      {page}
                    </Button>
                  );
                });
              })()}

              {/* Next Page */}
              <Button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                size="sm"
                variant="outline"
                borderColor="gray.200"
                color="gray.700"
                borderRadius="full"
                _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
              >
                {t("Next")}
              </Button>

              {/* Last Page */}
              <Button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                size="sm"
                variant="outline"
                borderColor="gray.200"
                color="gray.700"
                borderRadius="full"
                _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
                display={{ base: currentPage < totalPages - 2 ? "block" : "none", md: currentPage < totalPages - 1 ? "block" : "none" }}
              >
                {t("Last")}
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
      <Modal isOpen={showCreateUserModal} onClose={toggleCreateUserModal} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create User")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateUser onCreated={handleUserCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* Edit User Modal */}
<Modal isOpen={showEditUserModal} onClose={handleEditFormClose} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Edit User")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editableUser && (
              <Box as="form">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
                    {t("First Name")}
                  </FormLabel>
                  <Input
                    isRequired
                    type="text"
                    placeholder={t("First Name")}
                    mb="16px"
                    size="lg"
                    bg="white"
                    borderColor={GOLD_BORDER}
                    focusBorderColor={GOLD_BORDER}
                    value={editableUser.firstName}
                    onChange={(e) =>
                      setEditableUser({
                        ...editableUser,
                        firstName: e.target.value,
                      })
                    }
                  />
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
                    {t("Last Name")}
                  </FormLabel>
                  <Input
                    isRequired
                    type="text"
                    placeholder={t("Last Name")}
                    mb="16px"
                    size="lg"
                    bg="white"
                    borderColor={GOLD_BORDER}
                    focusBorderColor={GOLD_BORDER}
                    value={editableUser.lastName}
                    onChange={(e) =>
                      setEditableUser({
                        ...editableUser,
                        lastName: e.target.value,
                      })
                    }
                  />
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
                    {t("Email")}
                  </FormLabel>
                  <Input
                    isDisabled={isEditingSelf}
                    type="email"
                    placeholder={t("Email")}
                    mb="16px"
                    size="lg"
                    bg="white"
                    borderColor={GOLD_BORDER}
                    focusBorderColor={GOLD_BORDER}
                    value={editableUser.email}
                    onChange={(e) =>
                      setEditableUser({
                        ...editableUser,
                        email: e.target.value,
                      })
                    }
                  />
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
                    {t("Password")}
                  </FormLabel>
                  <InputGroup mb="16px" size="lg">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("Password (leave blank to keep unchanged)")}
                      bg="white"
                      borderColor={GOLD_BORDER}
                      focusBorderColor={GOLD_BORDER}
                      value={editableUser.password || ""}
                      onChange={(e) =>
                        setEditableUser({
                          ...editableUser,
                          password: e.target.value,
                        })
                      }
                      pr="40px"
                    />
                    <InputRightElement width="3rem" display="flex" alignItems="center" h="100%">
                      <Button
                        h="1.75rem"
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowPassword((show) => !show)}
                        tabIndex={-1}
                      >
                        {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
                    {t("Role")}
                  </FormLabel>
                  <Select
                isDisabled={!canEditRoleField}
          placeholder={t("Select Role")}
          mb="16px"
          size="lg"
          bg="white"
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
          value={editableUser.roleId}
          onChange={(e) =>
            setEditableUser({
              ...editableUser,
              roleId: e.target.value,
            })
          }
        >
         {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.roleName}
            </option>
          ))}
        </Select>

                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>{t("Status")}</FormLabel>
                  {typeof editableUser.isActive === 'boolean' ? (
                    <>
                      <Switch
                        isChecked={editableUser.isActive}
                        onChange={e => setEditableUser({
                          ...editableUser,
                          isActive: e.target.checked
                        })}
                        colorScheme="brand"
                        mr={2}
                      />
                      <Box as="span">
                        <Badge
                          colorScheme={editableUser.isActive ? "green" : "gray"}
                          variant="solid"
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="0.85em"
                        >
                          {editableUser.isActive ? t("Active") : t("Inactive")}
                        </Badge>
                      </Box>
                    </>
                  ) : (
                    <Badge colorScheme="gray" variant="subtle">{t(editableUser.isActive)}</Badge>
                  )}
                </FormControl>
              </Box>
            )}
          </ModalBody>
         <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
      <Button
        variant="outline"
        borderColor="gray.200"
        color="gray.700"
        onClick={handleEditFormClose}
      >
        {t("Cancel")}
      </Button>
      <Button
        bg={GOLD_BORDER}
        color="#1A1A1A"
        _hover={{ bg: GOLD_TEXT, color: "white" }}
        _active={{ bg: GOLD_TEXT, color: "white" }}
        onClick={handleSaveUser}
        leftIcon={<MdDone />}
        isLoading={isSavingUser}
      >
        {t("Save")}
      </Button>
    </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default UsersList;
